import { PaletteColor } from '@mui/material';
import { useMemo } from 'react';

import { theme } from '~/theme';
import { cssColorHexToRgbA } from '~/utils/helper';

export const useSxStyles = (props: {
  stepsCount: number;
  showTarget?: boolean;
}) => {
  const { stepsCount, showTarget } = props;

  return useMemo(() => {
    const lineWidth = ((stepsCount - 1) / stepsCount) * 100;
    const backgroundPadding = 2;
    const areaPadding = 1;
    const gradientOffset = theme.spacing(backgroundPadding + areaPadding + 3.5);

    const targetColor = theme.palette.grey[500];

    const backgroundFade = `linear-gradient(180deg, ${cssColorHexToRgbA(
      theme.palette.primary.main,
      0.06,
    )}, ${cssColorHexToRgbA(
      theme.palette.primary.main,
      0,
    )} 15%, ${cssColorHexToRgbA(
      theme.palette.primary.main,
      0,
    )} 70%, ${cssColorHexToRgbA(theme.palette.primary.main, 0.08)})`;

    const stepsBackgroundLine = () => {
      const midStepsCount = stepsCount - (showTarget ? 3 : 2);
      const lastPoint = ((stepsCount - 2) / (stepsCount - 1)) * 100;
      const firstPoint = 100 - lastPoint;
      const secondPoint = firstPoint * 2;

      const colors = [
        ...(showTarget
          ? [
              {
                color: targetColor,
              },
              {
                color: theme.palette.primary.main,
                point: firstPoint,
              },
            ]
          : [
              {
                color: theme.palette.primary.main,
              },
            ]),
        ...(midStepsCount > 0
          ? [
              {
                color: theme.palette.info.main,
                point: showTarget ? secondPoint : firstPoint,
              },
              {
                color: theme.palette.info.main,
                point: lastPoint,
              },
            ]
          : []),
        {
          color: theme.palette.success.main,
        },
      ];

      return `linear-gradient(to right, ${colors
        .map((spec) =>
          !!spec.point ? `${spec.color} ${spec.point}%` : spec.color,
        )
        .join(', ')})`;
    };

    const getPaletteColorAndBackgroundColor = (paletteColor: PaletteColor) => {
      return {
        backgroundColor: paletteColor.main,
        color: paletteColor.contrastText,
      };
    };

    return {
      root: {
        padding: theme.spacing(0, 0),
        backgroundImage: backgroundFade,
        backgroundSize: `100% 100%`,
        backgroundPosition: `left bottom`,
        backgroundRepeat: 'no-repeat',
        '& .createWorkflow': {
          textAlign: 'center',
          padding: theme.spacing(2, 0),
          '& .createWorkflow-content': {
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'center',
          },
        },
        '& .stateInfo': {
          padding: theme.spacing(2, 2),
        },
        '& .stepsRoot': {
          padding: theme.spacing(backgroundPadding, 0),
          overflow: 'auto',
          textAlign: 'center',
          '& .stepsSet': {
            display: 'inline-grid',
            gridTemplateColumns: `repeat(${stepsCount}, 1fr)`,
            backgroundImage: stepsBackgroundLine(),
            backgroundSize: `${lineWidth}% 3px`,
            backgroundPosition: `center ${gradientOffset}`,
            backgroundRepeat: 'no-repeat',
            '& .stepArea': {
              display: 'flex',
              justifyContent: 'center',
              padding: theme.spacing(areaPadding),
              alignItems: 'start',
              '& .stepBox': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: theme.spacing(2),
                padding: theme.spacing(1),
                boxShadow: 1,
                ...getPaletteColorAndBackgroundColor(theme.palette.info),
                '&.target': {
                  backgroundColor: targetColor,
                },
                '&.init': getPaletteColorAndBackgroundColor(
                  theme.palette.primary,
                ),
                '&.final': getPaletteColorAndBackgroundColor(
                  theme.palette.success,
                ),
                '& .stepNumber': {
                  height: '1.5em',
                  textAlign: 'center',
                  borderRadius: '50%',
                  fontWeight: 'bold',
                },
                '& .stepContent': {
                  marginTop: theme.spacing(0.5),
                  textAlign: 'center',
                  display: 'grid',
                  justifyItems: 'center',
                  '& .stepRole': {
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderRadius: '0.5em',
                    margin: theme.spacing(0.25, 0.25),
                    padding: theme.spacing(0, 0.5),
                    boxShadow: 1,
                  },
                  '& .relativeRole': {
                    fontSize: '0.8em',
                    marginTop: 0.5,
                  },
                },
              },
            },
          },
        },
      },
    };
  }, [stepsCount, showTarget]);
};
